import { DailyAudio, DailyProvider } from '@daily-co/daily-react'
import { trackEvent } from '@lib/clinician-tracking'
import React, {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import {
  type BlueprintDailyRoomConfiguration,
  useBlueprintDailyCall
} from '../hooks/use-blueprint-daily-call'

// Define the context type
export type PatientDailyManagerContextType = {
  leaveCall: () => Promise<void>
  isInCall: boolean
  roomConfiguration: BlueprintDailyRoomConfiguration
  isInWaitingRoom: boolean
  isCallEnded: boolean
  configureRoom: (props: BlueprintDailyRoomConfiguration) => void
  requestAccessToJoinCall: (props: { name: string }) => Promise<void>
  hasLeftCall: boolean
  isBackgroundBlurred: boolean
  toggleBackgroundBlur: () => void
  participantFullName: string
  setParticipantFullName: (fullName: string) => void
  isReadyToJoin: boolean
  setIsReadyToJoin: (isReadyToJoin: boolean) => void
}

// Create the context with default values
export const PatientDailyManagerContext =
  createContext<PatientDailyManagerContextType>({
    roomConfiguration: {
      url: ''
    },
    leaveCall: async () => {},
    isInCall: false,
    isInWaitingRoom: false,
    isCallEnded: false,
    configureRoom: () => {},
    requestAccessToJoinCall: async () => {},
    hasLeftCall: false,
    isBackgroundBlurred: false,
    toggleBackgroundBlur: () => {},
    participantFullName: '',
    setParticipantFullName: () => {},
    isReadyToJoin: false,
    setIsReadyToJoin: () => {}
  })

interface PatientDailyManagerProviderProps {
  children: ReactNode
}

export const PatientDailyManagerProvider: FC<
  PatientDailyManagerProviderProps
> = ({ children }) => {
  const [participantFullName, setParticipantFullName] = useState('')
  const [isReadyToJoin, setIsReadyToJoin] = useState(false)
  const {
    callObject,
    isInCall,
    isCallEnded,
    isInWaitingRoom,
    hasLeftCall,
    roomConfiguration,
    setRoomConfiguration,
    isBackgroundBlurred,
    toggleBackgroundBlur,
    _sharedOnJoinCall,
    leaveCall
  } = useBlueprintDailyCall(true)

  const requestAccessToJoinCall = async ({ name }: { name: string }) => {
    if (callObject?.isDestroyed()) {
      return
    }

    if (isInWaitingRoom || callObject?.meetingState() === 'joined-meeting') {
      await callObject?.leave()
    }

    await callObject?.join({
      url: roomConfiguration.url,
      sendSettings: {
        video: {
          // See: https://www.daily.co/blog/introducing-daily-adaptive-bitrate/
          allowAdaptiveLayers: true
        }
      }
    })

    // Once accepted into call, start call for non owner
    callObject?.on('access-state-updated', () => {
      // @ts-ignore
      if (callObject?.accessState()?.access.level === 'full') {
        trackEvent('event:telehealth-session.participant-joined-call')
        _sharedOnJoinCall()
      }
    })

    // Ask for permission to join the call
    await callObject?.requestAccess({
      name,
      access: {
        level: 'full'
      }
    })
  }

  const configureRoom = (config: BlueprintDailyRoomConfiguration) => {
    setRoomConfiguration(config)
  }

  return (
    <PatientDailyManagerContext.Provider
      value={{
        roomConfiguration,
        leaveCall,
        isInCall,
        isInWaitingRoom,
        isCallEnded,
        configureRoom,
        requestAccessToJoinCall,
        hasLeftCall,
        isBackgroundBlurred,
        toggleBackgroundBlur,
        participantFullName,
        setParticipantFullName,
        isReadyToJoin,
        setIsReadyToJoin
      }}
    >
      <DailyProvider callObject={callObject}>
        {children}
        <DailyAudio />
      </DailyProvider>
    </PatientDailyManagerContext.Provider>
  )
}

export const usePatientDailyManager = () => {
  return useContext(PatientDailyManagerContext)
}
